import { inject, Injectable, signal } from '@angular/core';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import {
    IOnboardingCreationTypeOption,
    IOnboardingEditItem,
    IOnboardingSection,
    IOnboardingStep,
    IOnboardingStepNotification,
    ISyncOnboardingEditItem,
} from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { AccessRightFilterCollectionModel } from '@fi-shared/models';
import { FiDialogsService } from '@fi-shared/fi-dialogs/fi-dialogs.service';
import { AppTextService } from '@fi-shared/app-text';
import {
    FiOnboardingAddDialogComponent,
    IOnboardingAddDialogResult,
} from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-add-dialog/fi-onboarding-add-dialog.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { OnboardingCreationTypes } from './onboarding-creation-types';
import { DeepReadonly } from '@fi-shared/types/deep-readonly';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import {
    FiOnboardingConfirmDeleteItemDialogComponent,
    IFiOnboardingConfirmDeleteItemDialogResult,
} from './fi-onboarding-edit/fi-onboarding-confirm-delete-item-dialog/fi-onboarding-confirm-delete-item-dialog.component';
import {
    FiOnboardingCategoryType,
    FiOnboardingDisplayType,
    FiOnboardingStepNotificationRecipientType,
    FiOnboardingStepNotificationReminderTime,
} from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { FiOnboardingAutomatedNotificationDialogComponent } from './fi-onboarding-edit/fi-onboarding-automated-notification-dialog/fi-onboarding-automated-notification-dialog.component';
import {
    FiOnboardingBuilderSharingSettingsStep,
    FiOnboardingBuilderStoreService,
    FiOnboardingBuilderTabIndex,
} from './fi-onboarding-builder-store.service';
import { IOnboardingBuilderSharingSettingsStep } from './fi-onboarding';
import { FiCustomerDataService } from '@fi-app/shared/fi-shared-services/fi-customer-data.service';
import { NgModel } from '@angular/forms';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { ObjectTypes } from '@fi-app/shared/types/object-type';
import { FiOnboardingService } from './fi-onboarding.service';
import { FiOnboardingElementDropListHelperService } from './fi-onboarding-edit/fi-onboarding-element-drop-list-helper.service';
import { FiItemSharingSettingsDialogService } from '@fi-app/shared/access-right-filters/fi-item-sharing-settings-dialog/fi-item-sharing-settings.service';

@Injectable()
export class FiOnboardingBuilderHelperService {
    private readonly fiContentLanguageService = inject(FiContentLanguageSharedService);
    private readonly fiDialogsService = inject(FiDialogsService);
    private readonly appTextService = inject(AppTextService);
    private readonly fiStateService = inject(FiStateService);
    private readonly fiCustomerDataService = inject(FiCustomerDataService);
    private readonly fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private readonly fiOnboardingService = inject(FiOnboardingService);
    private readonly fiOnboardingElementDropListHelperService = inject(FiOnboardingElementDropListHelperService);
    private readonly fiItemSharingSettingsDialogService = inject(FiItemSharingSettingsDialogService);

    getStepElementId(elementId: number, objectType: ObjectTypes): string {
        return `step-objid-${elementId}-objtype-${objectType}`;
    }

    readonly onboardingCreationTypeOptions: DeepReadonly<IOnboardingCreationTypeOption[]> = [
        {
            heading: this.appTextService.get('oboarding.create'),
            value: OnboardingCreationTypes.Default,
            description: this.appTextService.get('oboarding.create_description'),
        },
        {
            heading: this.appTextService.get('onboarding.connection'),
            value: OnboardingCreationTypes.Connection,
            description: this.appTextService.get('onboarding.connection_description'),
            isBannerShown: true,
            bannerText: this.appTextService.get('onboarding.connection_banner_text'),
        },
    ];

    private readonly syncEnabledSettingsSteps: IOnboardingBuilderSharingSettingsStep[] = [
        {
            Id: FiOnboardingBuilderSharingSettingsStep.Share,
            Name: this.appTextService.get('common.share'),
            IconClass: 'far fa-share-nodes',
        },
        {
            Id: FiOnboardingBuilderSharingSettingsStep.Category,
            Name: this.appTextService.get('common.category'),
            IconClass: 'far fa-shapes',
        },
        {
            Id: FiOnboardingBuilderSharingSettingsStep.Activate,
            Name: this.appTextService.get('common.activate'),
            IconClass: 'far fa-play',
        },
    ];
    private readonly syncDisabledSettingsSteps: IOnboardingBuilderSharingSettingsStep[] = [
        {
            Id: FiOnboardingBuilderSharingSettingsStep.Share,
            Name: this.appTextService.get('common.share'),
            IconClass: 'far fa-share-nodes',
        },
        {
            Id: FiOnboardingBuilderSharingSettingsStep.Activate,
            Name: this.appTextService.get('common.activate'),
            IconClass: 'far fa-play',
        },
    ];

    initStoreDataNewOnboarding(currentOnboarding: IOnboardingEditItem): void {
        const initialOnboarding = this.getNewOnboardingInstance();
        this.fiOnboardingBuilderStoreService.setInitialOnboarding(initialOnboarding);
        if (isNullOrUndefined(currentOnboarding)) {
            currentOnboarding = initialOnboarding;
        }
        currentOnboarding.Sections.forEach((section, sectionIndex) => {
            section.Steps.forEach((_, stepIndex) => {
                this.fiOnboardingElementDropListHelperService.addStepElementDropListId(sectionIndex, stepIndex);
            });
        });
        this.fiOnboardingBuilderStoreService.setCurrentOnboarding(currentOnboarding);
        const selectedLanguageId = this.fiContentLanguageService.getContentLanguageIdForDisplayContent(
            currentOnboarding.Names,
        );
        this.fiOnboardingBuilderStoreService.setSelectedLanguageId(selectedLanguageId);
    }

    initStoreDataExistingOnboarding(onboardingId: number): Promise<IOnboardingEditItem> {
        return new Promise((resolve, reject) => {
            this.fiOnboardingService.getOnboarding(onboardingId).then((onboarding) => {
                this._initiRequiredOnboardingData(onboarding);
                resolve(onboarding);
            }, reject);
        });
    }

    initStoreDataOnboardingCopy(onboardingId: number): Promise<IOnboardingEditItem> {
        return new Promise((resolve, reject) => {
            this.fiOnboardingService.getOnboardingCopy(onboardingId).then((onboarding) => {
                onboarding.IsActive = undefined;
                onboarding.Names = this.fiContentLanguageService.insertCopyLabelIntoContentLanguageLabelModels(
                    onboarding.Names,
                );
                this._initiRequiredOnboardingData(onboarding);
                resolve(onboarding);
            }, reject);
        });
    }

    private _initiRequiredOnboardingData(onboarding: IOnboardingEditItem): void {
        this.fiContentLanguageService.initializeContentLanguagesArray(onboarding.Names, onboarding.Names);
        this.fiContentLanguageService.initializeContentLanguagesArray(onboarding.Descriptions, onboarding.Descriptions);
        onboarding.Sections.forEach((section, sectionIndex) => {
            this.fiContentLanguageService.initializeContentLanguagesArray(section.Names, section.Names);
            this.fiContentLanguageService.initializeContentLanguagesArray(section.Descriptions, section.Descriptions);
            section.Steps.forEach((step, stepIndex) => {
                this.fiContentLanguageService.initializeContentLanguagesArray(step.Names, step.Names);
                this.fiContentLanguageService.initializeContentLanguagesArray(step.Descriptions, step.Descriptions);
                this.fiOnboardingElementDropListHelperService.addStepElementDropListId(sectionIndex, stepIndex);
            });
        });
        this.fiOnboardingBuilderStoreService.setInitialOnboarding(onboarding);
        const selectedLanguageId = this.fiContentLanguageService.getContentLanguageIdForDisplayContent(
            onboarding.Names,
        );
        this.fiOnboardingBuilderStoreService.setSelectedLanguageId(selectedLanguageId);
        this.fiOnboardingBuilderStoreService.setCurrentOnboarding(onboarding);
    }

    setInitialSavedQuickSelectionFilters(): void {
        const onboardingQuickSelectionFilters = this.fiOnboardingBuilderStoreService.onboardingQuickSelectionFilters();
        const onboarding = this.fiOnboardingBuilderStoreService.currentOnboarding();
        const selectedFiltersCollection = this.fiItemSharingSettingsDialogService.getSavedFilterBySharingSelection(
            onboarding.AccessRightFilterCollection.AccessRightCoreFilter,
            onboardingQuickSelectionFilters,
            true,
        );
        this.fiOnboardingBuilderStoreService.setSelectedFiltersCollection(selectedFiltersCollection);
    }

    getAvailableSettingsSteps(): IOnboardingBuilderSharingSettingsStep[] {
        return this.fiCustomerDataService.isSynchronizedOnboardingEnabled()
            ? this.syncEnabledSettingsSteps
            : this.syncDisabledSettingsSteps;
    }

    getNewOnboardingInstance(): IOnboardingEditItem {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Descriptions: this.fiContentLanguageService.getContentLanguageLabelModels(),
            IsShared: true,
            Category: FiOnboardingCategoryType.Independent,
            DisplayType: FiOnboardingDisplayType.HomePage,
            FilterUsersByActivationDate: false,
            AccessRightFilterCollection: AccessRightFilterCollectionModel.getNewInstance(),
            MediaBankFileReferences: [],
            Sections: [this.getNewOnboardingSectionInstance()],
            IsActive: undefined,
        };
    }

    getNewSyncOnboardingInstance(): ISyncOnboardingEditItem {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Descriptions: this.fiContentLanguageService.getContentLanguageLabelModels(),
            DisplayType: FiOnboardingDisplayType.HomePage,
            Category: FiOnboardingCategoryType.Synchronized,
            IsActive: undefined,
            MasterOnboardingId: undefined,
            ConnectedOnboardingId: undefined,
            Conditions: [],
            MediaBankFileReferences: [],
        };
    }

    getNewOnboardingSectionInstance(): IOnboardingSection {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Descriptions: this.fiContentLanguageService.getContentLanguageLabelModels(),
            IsDisabled: false,
            Steps: [this.getNewOnboardingStepInstance()],
        };
    }

    getNewOnboardingStepInstance(): IOnboardingStep {
        return {
            Names: this.fiContentLanguageService.getContentLanguageLabelModels(),
            Descriptions: this.fiContentLanguageService.getContentLanguageLabelModels(),
            IsDisabled: false,
            Notifications: [],
            Element: undefined,
        };
    }

    getNewOnboardingStepNotificationInstance(): IOnboardingStepNotification {
        return {
            RecipientType: FiOnboardingStepNotificationRecipientType.Participants,
            ReminderTime: FiOnboardingStepNotificationReminderTime.Completed,
            ViaNotification: false,
            ViaSMS: false,
            ViaEmail: false,
            UserIds: [],
            RoleIds: [],
            UnitIds: [],
            UserContacts: [],
        };
    }

    openAddOnboardingDialog(): void {
        this.fiDialogsService
            .custom({
                contentComponentRef: FiOnboardingAddDialogComponent,
                headerText: this.appTextService.get('onboarding.create_new_onboarding'),
                isHeaderDividerShown: true,
                headerDividerClass: 'BrandedBorderColor',
                noButtonText: this.appTextService.get('common.cancel'),
                yesButtonText: this.appTextService.get('common.proceed'),
                preventUppercaseButtonLabel: true,
                hideDialogTopBorder: true,
                config: {
                    panelClass: ['fi-rounded-dialog-panel', 'fi-rounded-dialog-full-round-buttons'],
                    width: '852px',
                    maxWidth: '90%',
                    autoFocus: false,
                },
            })
            .onYesButtonClick((onboarding: IOnboardingAddDialogResult) => {
                if (onboarding.isDefaultOnboarding) {
                    this.fiStateService.go(FiStateNames.Onboarding.Add, { onboarding: onboarding.onboarding });
                } else {
                    this.fiStateService.go(FiStateNames.Onboarding.Connection.Add, {
                        onboarding: onboarding.onboarding,
                    });
                }
            });
    }

    openConfirmDeleteItemNotActiveDialog(
        headerText: string,
        messageText: string,
        yesButtonText?: string,
    ): IFiDialogResult<unknown> {
        return this.fiDialogsService.confirmSimpleDelete({
            headerIconClass: 'far fa-circle-exclamation red',
            headerText: headerText,
            messageText: messageText,
            preventUppercaseButtonLabel: true,
            isTopCloseButtonShown: false,
            isBigSizeTopBorder: true,
            yesButtonText: yesButtonText || this.appTextService.get('common.delete'),
            config: {
                panelClass: ['fi-confirm-delete-dialog-panel', 'fi-rounded-dialog-panel'],
                maxWidth: '560px',
                width: '90%',
                autoFocus: false,
            },
        });
    }

    openConfirmDeleteActiveItemDialog(
        headerText: string,
        messageText: string,
    ): IFiDialogResult<IFiOnboardingConfirmDeleteItemDialogResult> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiOnboardingConfirmDeleteItemDialogComponent,
            contentComponentData: { message: messageText },
            headerText: headerText,
            headerIconClass: 'far fa-circle-exclamation red',
            colorClass: 'red',
            isTopCloseButtonShown: false,
            isBigSizeTopBorder: true,
            hideFooter: true,
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-dialog-content-no-bottom-padding'],
                maxWidth: '560px',
                width: '90%',
                autoFocus: false,
            },
        });
    }

    openAutomatedNotificationDialog(
        notifications: IOnboardingStepNotification[],
    ): IFiDialogResult<IOnboardingStepNotification[]> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiOnboardingAutomatedNotificationDialogComponent,
            contentComponentData: notifications,
            hideHeader: true,
            hideFooter: true,
            hideDialogTopBorder: true,
            config: {
                panelClass: [
                    'fi-rounded-dialog-panel',
                    'fi-dialog-content-no-bottom-padding',
                    'fi-automated-notification-dialog-panel',
                ],
                maxWidth: '810px',
                width: '90%',
                autoFocus: false,
                disableClose: true,
            },
        });
    }

    private _currentOnboardingInvalidModels = signal<NgModel[]>([]);
    currentOnboardingInvalidModels = this._currentOnboardingInvalidModels.asReadonly();
    setCurrentOnboardingInvalidModels(invalidModels: NgModel[]): void {
        this._currentOnboardingInvalidModels.set(invalidModels);
    }
    resetCurrentOnboardingInvalidModels(): void {
        this._currentOnboardingInvalidModels.set([]);
    }

    handleOnboardingEditFormError(invalidModels: NgModel[]): void {
        this.setCurrentOnboardingInvalidModels(invalidModels);
        this.showMissingMandatoryInfoDialog();
        if (this.isSectionInvalid(invalidModels, this.onboardingEditorModelsNames)) {
            this.fiOnboardingBuilderStoreService.setSelectedTabIndex(FiOnboardingBuilderTabIndex.OnboardingEditor);
        } else {
            this.fiOnboardingBuilderStoreService.setSelectedTabIndex(FiOnboardingBuilderTabIndex.SettingsSharing);
            this.updateSharingSettingsStepBasedOnInvalidModels(invalidModels);
        }
    }

    showMissingMandatoryInfoDialog(): void {
        this.fiDialogsService.notify({
            messageText: this.appTextService.get('common.error_all_mandatory'),
            headerText: this.appTextService.get('error.missing_mandatory_info'),
            headerIconClass: 'far fa-exclamation-circle red',
            isBigSizeTopBorder: true,
            colorClass: 'red',
            yesButtonText: this.appTextService.get('ok'),
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                autoFocus: false,
            },
        });
    }

    private readonly onboardingEditorModelsNames = ['onboarding-name', 'section', 'section-name'];
    private readonly onbaordingActivateSettingsModelNames = ['activate-settings'];
    private readonly onbaordingSharingSettingsModelNames = [
        'sharing-status',
        'activation-date',
        'access-right-settings',
    ];

    private isSectionInvalid(invalidModels: NgModel[], sectionModelNames: string[]): boolean {
        const modelNames = invalidModels
            .map((model) => model.name)
            .filter((name) => !isNullOrUndefined(name) && !!name.trim().length);
        return sectionModelNames.some((editorModelName) => {
            return modelNames.some((modelName) => modelName.includes(editorModelName));
        });
    }

    updateSharingSettingsStepBasedOnInvalidModels(invalidModels: NgModel[]): void {
        let invalidStep: FiOnboardingBuilderSharingSettingsStep;
        if (this.isSectionInvalid(invalidModels, this.onbaordingSharingSettingsModelNames)) {
            invalidStep = FiOnboardingBuilderSharingSettingsStep.Share;
        } else if (this.isSectionInvalid(invalidModels, this.onbaordingActivateSettingsModelNames)) {
            invalidStep = FiOnboardingBuilderSharingSettingsStep.Activate;
        }
        this.fiOnboardingBuilderStoreService.setSelectedSettingsStep(invalidStep);
    }

    readonly sectionListComponentModelName = '';

    private readonly sectionComponentModelName = '';
    getSectionComponentModelName(sectionIndex: number): string {
        return `${this.sectionComponentModelName}-${sectionIndex}`;
    }

    private readonly stepComponentModelName = '';
    getStepComponentModelName(sectionIndex: number, stepIndex: number): string {
        return `${this.getSectionComponentModelName(sectionIndex)}-${this.stepComponentModelName}-${stepIndex}`;
    }
}
